import { Check, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import {
  addDays,
  DAY_KEYS,
  DAY_LABELS_SHORT,
  type DayKey,
  todayDayKeyInRange,
} from "@/lib/days";
import type { WeekData } from "@/lib/schema";
import { getWidgetModule, isDayWidgetEntry } from "@/widgets/registry";

interface Props {
  weekId: string;
  week: WeekData;
  selected: DayKey | "week";
}

function scoreFor(week: WeekData, dayKey: DayKey): number | null {
  if (week.widgets.length === 0) return null;
  let total = 0;
  let counted = 0;
  for (const w of week.widgets) {
    if (!w.config.enabled) continue;
    if (!isDayWidgetEntry(w)) continue;
    const module = getWidgetModule(w.config.kind);
    if (module?.scope !== "day") continue;
    if (module.scoring !== "points" || !module.scoreDay) continue;
    total += module.scoreDay(w.config, w.days[dayKey], dayKey, w.week);
    counted += 1;
  }
  return counted > 0 ? total : null;
}

function weekTotals(week: WeekData): { score: number; goal: number } {
  let score = 0;
  let goal = 0;
  for (const w of week.widgets) {
    if (!w.config.enabled) continue;
    if (!isDayWidgetEntry(w)) continue;
    const module = getWidgetModule(w.config.kind);
    if (module?.scope !== "day") continue;
    if (module.scoring !== "points") continue;
    score += module.scoreWeek(w.config, { week: w.week, days: w.days });
    const wg = (w.config as { weekGoal?: number }).weekGoal;
    if (typeof wg === "number" && Number.isFinite(wg)) goal += wg;
  }
  return { score, goal };
}

function isPast(dateIso: string, todayIso: string): boolean {
  return dateIso < todayIso;
}

function todayIsoLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function DayStrip({ weekId, week, selected }: Props) {
  const { score, goal } = weekTotals(week);
  const encoded = encodeURIComponent(weekId);
  const todayKey = todayDayKeyInRange(week.startDate, new Date());
  const todayIso = todayIsoLocal();
  const completed = week.completedDays ?? {};
  const isOpen = week.state === "open";

  return (
    <div className="daystrip" data-testid="day-strip">
      {DAY_KEYS.map((k, i) => {
        const isSelected = k === selected;
        const isToday = k === todayKey;
        const dateIso = addDays(week.startDate, i);
        const day = Number(dateIso.slice(8, 10));
        const dayScore = scoreFor(week, k);
        const isCompleted = completed[k] === true;
        const past = isPast(dateIso, todayIso);
        const isBad = isOpen && past && !isCompleted;
        const classes = [
          isSelected ? "selected" : "",
          isToday ? "today" : "",
          isCompleted ? "completed" : "",
          isBad ? "bad" : "",
        ]
          .filter(Boolean)
          .join(" ");
        return (
          <Link
            key={k}
            to={`/uke/${encoded}/${k}`}
            className={classes}
            data-testid={`day-tile-${k}`}
          >
            <div className="dlabel" data-testid={`day-tile-label-${k}`}>
              {isToday ? "I dag" : DAY_LABELS_SHORT[k]}
              <span className="dlabel-date"> · {day}.</span>
            </div>
            <div className="ddate" data-testid={`day-tile-score-${k}`}>
              {dayScore === null ? "—" : dayScore}
              <span className="pts-unit">p</span>
            </div>
            {isCompleted ? (
              <span
                className="dbadge good"
                role="img"
                aria-label="Fullført"
                title="Fullført"
                data-testid={`day-tile-completed-${k}`}
              >
                <Check />
              </span>
            ) : null}
            {isBad ? (
              <span
                className="dbadge bad"
                role="img"
                aria-label="Ikke fullført"
                title="Ikke fullført"
                data-testid={`day-tile-overdue-${k}`}
              >
                <TriangleAlert />
              </span>
            ) : null}
          </Link>
        );
      })}
      <Link
        to={`/uke/${encoded}`}
        className={`week-btn ${selected === "week" ? "selected" : ""}`}
        data-testid="day-tile-week"
      >
        <div className="dlabel">
          <span className="dlabel-full">Ukesoppsummering</span>
          <span className="dlabel-short">Uke</span>
        </div>
        {goal > 0 ? (
          <div className="ddate">
            <span className="week-detail">
              <span data-testid="week-tile-points">{score}</span>
              <span> / </span>
              <span data-testid="week-tile-goal">{goal}</span>
              <span className="pts-unit">p</span>
              <span> · </span>
            </span>
            <span data-testid="week-tile-percentage">
              {Math.round((score / goal) * 100)}%
            </span>
          </div>
        ) : null}
      </Link>
    </div>
  );
}
